// src/redux/selectors/assessmentSelectors.js
import { createSelector } from '@reduxjs/toolkit';

// Base slices
export const selectRiskFormData    = (state, modelId) => state.risks.models[modelId]?.riskFormData || {};
export const selectControlsState   = state => state.controls.savedControls || {};
export const selectResidualByModel = (state, modelId) => state.residualRisk.byModel[modelId] || {};
export const selectSecurityData    = (state, modelId) => state.securityReportData.dataByModel?.[modelId] || {};
export const selectContextData     = (state, modelId) => state.context.data[modelId] || {};

// 1) Flatten model risks into entries with controls & residual rating
export const selectRiskEntries = createSelector(
  [ selectRiskFormData, selectControlsState, selectResidualByModel ],
  (riskFormData, controlsState, residualByModel) =>
    Object.entries(riskFormData).flatMap(
      ([section, catObj]) =>
        Object.entries(catObj.sectionRisks || {}).flatMap(
          ([category, risks]) =>
            (risks || []).map(risk => ({
              riskId:   risk.riskId,
              title:    risk.riskName,
              section,
              category,
              priority: risk.priority,
              residual:
                residualByModel[risk.riskId]?.overridden?.residual_rating ||
                residualByModel[risk.riskId]?.residual_rating ||
                'N/A',
              controls: controlsState[risk.riskId] || []
            }))
        )
    )
);

// 2) Map riskId → savedControls array
export const selectAllControls = createSelector(
  [ selectControlsState ],
  controlsState => {
    const all = {};
    Object.entries(controlsState).forEach(([rid, list]) => {
      all[rid] = Array.isArray(list) ? list : [];
    });
    return all;
  }
);

// 3) Security risks & controls
export const selectSecurityRisks = createSelector(
  [ selectSecurityData ],
  data => Array.isArray(data.risks) ? data.risks : []
);

export const selectSecurityControls = createSelector(
  [ selectSecurityData ],
  data => data.controls || {}
);

// 4) Context nugget & narratives
export const selectContextNugget = createSelector(
  [ selectContextData ],
  ctx => ({
    useCase:         ctx.useCase?.narrative || '',
    businessContext: ctx.businessContext || '',
    modelContext:    ctx.modelContext || ''
  })
);

export const selectNarratives = createSelector(
  [ selectContextData ],
  ctx =>
    Object.entries(ctx).reduce((acc, [section, val]) => {
      acc[section] = val.narrative || `No narrative for ${section}`;
      return acc;
    }, {})
);

// 5) Chart data: priority breakdown (model vs security)
export const selectRiskPriorityChartData = createSelector(
  [ selectRiskEntries, selectSecurityRisks ],
  (entries, securityRisks) => {
    const counts = {};
    // Initialize for any found priorities
    entries.forEach(e => { counts[e.priority] = counts[e.priority] || { model:0, security:0 }; });
    securityRisks.forEach(r => { counts[r.threat_level] = counts[r.threat_level] || { model:0, security:0 }; });
    // Tally
    entries.forEach(e => counts[e.priority].model++);
    securityRisks.forEach(r => counts[r.threat_level].security++);
    // Flatten
    return Object.entries(counts).map(([name, {model, security}]) => ({ name, model, security }));
  }
);

// 6) Chart data: section distribution
export const selectSectionDistributionData = createSelector(
  [ selectRiskEntries, selectSecurityRisks ],
  (entries, securityRisks) => {
    const bySection = {};
    entries.forEach(e => {
      bySection[e.section] = bySection[e.section] || { count:0, risks:[] };
      bySection[e.section].count++;
      bySection[e.section].risks.push(e.title);
    });
    bySection['Security'] = {
      count: securityRisks.length,
      risks: securityRisks.map(r => r.risk_name || r.title)
    };
    return Object.entries(bySection).map(([name, data]) => ({ name, ...data }));
  }
);

export const makeSelectNarrative = section => createSelector(
  [selectContextData],
  ctx => ctx[section]?.narrative || ''
);